import { cn, focusRing } from './cn';

// Kept apart from button.tsx, a client module, so a server component can give a link the button's
// look.

const variants = {
  /** The one main action of a zone, in the service's accent. */
  primary:
    'bg-crow-accent text-crow-on-accent hover:bg-[color-mix(in_oklch,var(--crow-accent)_88%,var(--crow-text))] active:bg-[color-mix(in_oklch,var(--crow-accent)_78%,var(--crow-text))]',
  secondary: 'border border-crow-line-strong bg-crow-surface text-crow-text hover:bg-crow-raised',
  ghost: 'text-crow-text-2 hover:bg-crow-raised hover:text-crow-text',
  /** Removes or revokes, but can be undone or is confirmed elsewhere. */
  danger:
    'border border-[color-mix(in_oklch,var(--crow-danger)_35%,var(--crow-surface))] text-crow-danger hover:bg-crow-danger-tint',
  /** The confirmation of an irreversible action, inside its dialog. */
  'danger-solid':
    'bg-crow-danger text-crow-on-danger hover:bg-[color-mix(in_oklch,var(--crow-danger)_88%,var(--crow-text))]',
} as const;

const sizes = {
  sm: 'h-7 gap-1.5 px-2.5 text-crow-compact [&_svg]:size-3.5',
  md: 'h-8 gap-2 px-3 text-crow-compact [&_svg]:size-4',
  lg: 'h-10 gap-2 px-4 text-crow-body [&_svg]:size-4',
  icon: 'size-8 [&_svg]:size-4',
  'icon-sm': 'size-7 [&_svg]:size-3.5',
} as const;

export type ButtonVariant = keyof typeof variants;
export type ButtonSize = keyof typeof sizes;

/**
 * The button's look without its size, for a project that sizes its buttons itself (crowdex, on
 * its density-aware control heights).
 */
export function buttonLookClass(variant: ButtonVariant = 'secondary', className?: string) {
  return cn(
    'inline-flex shrink-0 items-center justify-center rounded-crow-control font-semibold whitespace-nowrap transition-colors duration-[var(--crow-dur-1)] active:translate-y-px disabled:pointer-events-none disabled:border-transparent disabled:bg-crow-raised disabled:text-crow-text-3 [&_svg]:shrink-0',
    focusRing,
    variants[variant],
    className,
  );
}

export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
) {
  return buttonLookClass(variant, cn(sizes[size], className));
}
